import React, { useCallback, useMemo, useState } from 'react'
import { Column, TableConfig } from '../../types/types';
import { ContactRoles } from './EditRolePermissions';
import DataTable from '../../components/listing/DataTable';
import { DropResult } from '@hello-pangea/dnd';

interface contactListings {
    contactRoles: ContactRoles[];
    isLoading: boolean;
}

interface contact {
    companyId: number;
    email: string;
    firstName: string;
    lastName: string;
    id: number;
    signedUrl: string;
}

interface GroupedContactResult {
  contact: contact;
  organizationNames: string;
  orgSignedUrl: string;
  contactOrgRoleId: number;
}

export function groupContactsByOrganizationNames(
  data: ContactRoles[]
): GroupedContactResult[] {

  const contactMap: Record<
    number,
    {
      contact: any;
      organizationNames: string[];
      contactOrgRoleId: number;
      orgSignedUrl: string;
    }
  > = {};

  for (const item of data) {
    const co = item?.contactOrganization;

    if (!co?.contact) continue;

    const { contact, organization } = co;
    const key = contact.id;

    if (!contactMap[key]) {
      contactMap[key] = {
        contact: { ...contact },
        organizationNames: [],
        contactOrgRoleId: item.id,
        orgSignedUrl: organization?.signedUrl || "",
      };
    }

    if (organization?.name) {
      contactMap[key].organizationNames.push(organization.name);
    }
  }

  return Object.values(contactMap).map((entry) => {

    const uniqueOrgNames = entry.organizationNames.filter(
      (name: string, index: number, self: string[]) =>
        self.indexOf(name) === index
    );

    return {
      contact: entry.contact,
      organizationNames: uniqueOrgNames.join(", "),
      contactOrgRoleId: entry.contactOrgRoleId,
      orgSignedUrl: entry.orgSignedUrl,
    };
  });
}

const AssignedContactsListing: React.FC<contactListings> = ({ contactRoles, isLoading }) => {
    const columns = useMemo<Column[]>(() => [
        { id: 'primaryUser', label: 'Name', isSortable: false },
        { id: 'email', label: 'Email', isSortable: false },
        { id: 'organization', label: 'Organization Name', isSortable: false },
      ], []);
    

    const filterData = groupContactsByOrganizationNames(contactRoles);

    const tableData = 
    (filterData as GroupedContactResult[]).map((item) => ({
        contact_id: item?.contact?.id,
        contactId: item?.contact?.id,
        contactFirstName: item?.contact?.firstName,
        contactLastName: item?.contact?.lastName,
        contactSignedUrl: item?.contact?.signedUrl,
        email: item?.contact?.email,
        organization: item.organizationNames,
      }));

    const STORAGE_KEY = `tableConfig_ignore`;
    const [tableConfig, setTableConfig] = useState<TableConfig>(() => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        return saved
        ? JSON.parse(saved)
        : {
            visibleColumns: columns.filter(col => col.id !== "id").map(col => col.id),
            page: 1,
            perPage: 10,
            sort: undefined,
            filters: {},
            query: "",
            columnOrder: columns.map(col => col.id),
        };
    });
    
    const handleDragEnd = useCallback(
        (result: DropResult) => {
        if (!result.destination) return;
        const newColumnOrder = Array.from(tableConfig.columnOrder);
        const [removed] = newColumnOrder.splice(result.source.index, 1);
        newColumnOrder.splice(result.destination.index, 0, removed);
        setTableConfig(prev => ({ ...prev, columnOrder: newColumnOrder }));
        },
        [tableConfig.columnOrder]
    );

  return (
    <DataTable
        routeKey='contacts'
        title='Contacts Listing'
        hideActionColumn={true}
        noAction={true}
        columns={columns}
        data={tableData}
        columnOrder={tableConfig.columnOrder}
        visibleColumns={tableConfig.visibleColumns}
        sort={tableConfig.sort}
        noDataTitle="No Contact Found"
        noDataSubtitle="No contact found against this role."
        onDragEnd={handleDragEnd}
        loading={isLoading}
        perPage={tableConfig.perPage}
        onAction={() => {}}
        minWidth="1000px"
        skeletonHeight='32px'
    />
  )
}

export default AssignedContactsListing